import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE_NAME = "CSSBuy Spreadsheet";

// The site origin is provided by the platform via VITE_SITE_URL.
// Fall back to the project's canonical domain only when it is not yet injected.
export const SITE_URL = ((import.meta.env.VITE_SITE_URL as string | undefined) || "https://cssbuybr.quest").replace(/\/+$/, "");

// Favicon provided in the authoritative SEO data — also reused as the site logo.
export const SITE_LOGO = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png";

export interface SeoData {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  image?: string;
  type?: string;
  noindex?: boolean;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr: "name" | "property", key: string) {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function getCanonicalUrl(path: string): string {
  const clean = path === "/" ? "/" : path;
  return `${SITE_URL}${clean}`;
}

/**
 * Centralized, route-aware SEO/head manager.
 * On every route change it (re)sets the title, description, keywords,
 * canonical, Open Graph and Twitter Card tags so metadata never leaks
 * from one route to the next.
 */
export function useSeo(data: SeoData) {
  const location = useLocation();

  useEffect(() => {
    const path = data.canonicalPath ?? location.pathname;
    const canonicalUrl = getCanonicalUrl(path);

    document.title = data.title;

    upsertMeta("name", "description", data.description);
    if (data.keywords) {
      upsertMeta("name", "keywords", data.keywords);
    } else {
      removeMeta("name", "keywords");
    }

    upsertCanonical(canonicalUrl);

    // Open Graph
    upsertMeta("property", "og:title", data.title);
    upsertMeta("property", "og:description", data.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:type", data.type || "website");
    if (data.image) {
      upsertMeta("property", "og:image", data.image);
    } else {
      removeMeta("property", "og:image");
    }

    // Twitter Card
    upsertMeta("name", "twitter:card", data.image ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", data.title);
    upsertMeta("name", "twitter:description", data.description);
    if (data.image) {
      upsertMeta("name", "twitter:image", data.image);
    } else {
      removeMeta("name", "twitter:image");
    }

    // Robots
    if (data.noindex) {
      upsertMeta("name", "robots", "noindex, nofollow");
    } else {
      removeMeta("name", "robots");
    }
  }, [location.pathname, data]);
}